import { gql } from '@apollo/client';
import {
  PROPERTY_COMPLETE_DATA_FRAGMENT,
  PROPERTY_CLICKTHROUGHS_FRAGMENT,
} from 'graphql/property/fragments';
import { LEASE_OPTION_FRAGMENT, LEASE_SEARCH_RESULT_FIELDS } from './fragments';
import {
  USER_BASIC_FIELDS,
  ID_NAME_FIELDS,
  BROKER_ID_NAME_FIELDS,
  COUNTRY_FIELDS,
  ID_NAME_WEBSITE_LOGO_FIELDS,
  LEASE_ANALYTICS_ITEM_FIELDS,
  PROPERTY_MAIN_FIELDS,
  PAGINATION_FIELDS,
} from 'graphql/common/fragments';

export const SEARCH_LEASES_QUERY = gql`
  query leases($search: LeaseSearchInput!) {
    leases(search: $search) {
      ${PAGINATION_FIELDS}
      results {
        ${LEASE_SEARCH_RESULT_FIELDS}
        property {
          ...propertyCompleteDataFragment
        }
      }
    }
  }
  ${PROPERTY_COMPLETE_DATA_FRAGMENT}
`;

export const SEARCH_LEASES_CLICKTHROUGHS_QUERY = gql`
  query leases($search: LeaseSearchInput!) {
    leases(search: $search) {
      ${PAGINATION_FIELDS}
      results {
        baseRent
        country {
          ${COUNTRY_FIELDS}
        }
        currencyCode
        escalation
        escalationIncrement
        escalationFormatted
        escalationType
        expirationDate
        floor
        freeRentMonths
        id
        landlordBrokers {
          ${BROKER_ID_NAME_FIELDS}
        }
        measurementSystem
        property {
          ...propertyClickthroughsFragment
        }
        rentType
        signDate
        size
        sizeMt
        tenantBrokers {
          ${BROKER_ID_NAME_FIELDS}
        }
        tenantCompany {
          ${ID_NAME_FIELDS}
        }
        tenantImprovementAllowance
        type
        transactionType
        leaseType {
          ${ID_NAME_FIELDS}
        }
        termInMonths
        comments
      }
    }
  }
  ${PROPERTY_CLICKTHROUGHS_FRAGMENT}
`;

export const SEARCH_LEASES_FIND_COMPS_QUERY = gql`
  query leases($search: LeaseSearchInput!) {
    leases(search: $search) {
      ${PAGINATION_FIELDS}
      results {
        id
        signDate
        type
        size
        sizeMt
        floor
        transactionType
        transactionTypeFormatted
        measurementSystem
        country {
          ${COUNTRY_FIELDS}
        }
        rentType
        escalation
        escalationIncrement
        escalationIncrementType {
          ${ID_NAME_FIELDS}
        }
        escalationType
        escalationFormatted
        tenantImprovementAllowance
        termInMonths
        freeRentMonths
        baseRent
        buildOutType {
          ${ID_NAME_FIELDS}
        }
        currencyCode
        expirationDate
        taxAndOpex
        netEffectiveRent
        netEffectiveRentCustom
        netEffectiveRentMonthly
        timeMeasurement
        leaseType {
          ${ID_NAME_FIELDS}
        }
        tenantCompany {
          ${ID_NAME_FIELDS}
        }
        tenantBrokers {
          ${BROKER_ID_NAME_FIELDS}
        }
        landlordBrokers {
          ${BROKER_ID_NAME_FIELDS}
        }
        property {
          id
          primaryAddress
          displayAddress
          measurementSystem
        }
      }
    }
  }
`;

export const LEASES_MKT_RENT_AVG_QUERY = gql`
  query leasesMarketRentAverageNew($input: LeasesMarketRentAverageInput) {
    leasesMarketRentAverageNew(input: $input) {
      totalAverage
      averageType
      averages {
        date
        value
        valueMonthly
        NNNEquivValue
        NNNEquivValueMontly
        propertyValue
        totalSizeSF
        totalSizeSquareMeters
        measurementSystem
        leasesCount
        currencyCode
        topItems
      }
    }
  }
`;

export const LEASES_QUERY = gql`
  query leases($search: LeaseSearchInput!) {
    leases(search: $search) {
      results {
        id
        signDate
        baseRent
        rentType
        baseRentFullServiceEquiv
        baseRentNNNEquiv
        baseRentYard
        size
        sizeMt
        measurementSystem
        currencyCode
      }
    }
  }
`;

export const GRAPH_LEASES_QUERY = gql`
  query graphLeases($input: GraphLeasesInput!) {
    graphLeases(input: $input) {
      rentType
      items {
        ${LEASE_ANALYTICS_ITEM_FIELDS}
      }
    }
  }
`;

export const SEARCH_LEASES_COMPANY_QUERY = gql`
  query leases($search: LeaseSearchInput!) {
    leases(search: $search) {
      ${PAGINATION_FIELDS}
      results {
        ${LEASE_SEARCH_RESULT_FIELDS}
        property {
          ${PROPERTY_MAIN_FIELDS}
          propertyCountry {
            ${COUNTRY_FIELDS}
          }
          status {
            ${ID_NAME_FIELDS}
          }
        }
      }
    }
  }
`;

export const LEASES_EXPIRATION_SIZED_QUERY = gql`
  query leasesExpirationSized($search: LeasesExpirationSizedInput) {
    leasesExpirationSized(search: $search) {
      propertyLeasesExpirationSizedResponse {
        propertyId
        expirationSizedResponse {
          text
          value
          valueMt
          count
          measurementSystem
        }
      }
      aggregateLeasesExpirationSizedResponse {
        text
        value
        valueMt
        count
        measurementSystem
      }
    }
  }
`;

export const LEASES_CAROUSEL_QUERY = gql`
  query leasesCarousel($input: LeasesCarouselInput!) {
    leasesCarousel(input: $input) {
      items {
        id
        additionalConcessions
        additionalConcessionsPSF
        additionalConcessionsPSM
        commencementDate
        comments
        signDate
        expandingContracting {
          ${ID_NAME_FIELDS}
        }
        type
        transactionType
        size
        sizeMt
        measurementSystem
        floor
        rentType
        freeRentType {
          ${ID_NAME_FIELDS}
        }
        leaseOptions {
          ...leaseOption
        }
        escalation
        escalationFormatted
        tenantImprovementAllowance
        termInMonths
        freeRentMonths
        baseRent
        currencyCode
        expirationDate
        taxAndOpex
        tenantCompany {
          ${ID_NAME_FIELDS}
        }
        tenantBroker {
          ${BROKER_ID_NAME_FIELDS}
        }
        landlordBroker {
          ${BROKER_ID_NAME_FIELDS}
        }
        property {
          id
          name
          primaryAddress
          secondaryAddress
          tertiaryAddress
          quaternaryAddress
          numberOfAlternativeAddresses
          displayAddress
          coverMediaUrl
          coverMediaType
          latitude
          longitude
          latitudeDisplay
          longitudeDisplay
          propertyClass {
            ${ID_NAME_FIELDS}
          }
          market {
            ${ID_NAME_FIELDS}
          }
          submarket {
            ${ID_NAME_FIELDS}
          }
          propertyType {
            ${ID_NAME_FIELDS}
          }
        }
        createdAt
        createdUser {
          ${USER_BASIC_FIELDS}
        }
        updatedAt
        updatedUser {
          ${USER_BASIC_FIELDS}
        }
      }
    }
  }
  ${LEASE_OPTION_FRAGMENT}
`;

export const GRAPH_OWNER_PORTFOLIO_LEASES_QUERY = gql`
  query graphOwnerPortfolioLeases($input: GraphOwnerPortfolioInput!) {
    graphOwnerPortfolioLeases(input: $input) {
      totalSize
      totalSizeMt
      leases {
        id
        size
        sizeMt
        measurementSystem
        propertyId
        property {
          name
          status
          propertyClass
          propertyType
          market
          marketId
          submarketId
          submarket
          micromarket
          primaryAddress
          coverMediaUrl
          latitude
          longitude
        }
      }
    }
  }
`;

export const GET_LEASE_QUERY = gql`
  query getLease($id: ID!) {
    lease(id: $id) {
      id
      owners {
        ${ID_NAME_FIELDS}
      }
        leaseType {
          ${ID_NAME_FIELDS}
        }
      expandingContracting {
        id
        name
      }
      type
      signDate
      size
      sizeMt
      warehouseSf
      warehouseMt
      officeSize
      officeSizeMt
      driveInDoors
      loadingDocks
      leaseOptions {
        ...leaseOption
      }
      parkingSpaces
      additionalParking
      trailerParkingSpaces
      priorAddress {
        ${ID_NAME_FIELDS}
        primaryAddress
      }
      priorSize
      measurementSystem
      baseRent
      baseRentFullServiceEquiv
      baseRentFullServiceEquivAnnual
      baseRentFullServiceEquivMonthly
      baseRentNNNEquivAnnual
      baseRentNNNEquivMonthly
      baseRentPerKw
      buildOutType {
        ${ID_NAME_FIELDS}
      }
      rentType
      rentPaymentDetail
      escalation
      escalationIncrement
      escalationIncrementType {
        ${ID_NAME_FIELDS}
      }
      escalationFormatted
      escalationType
      escalationCap
      escalationCollar
      upwardOnly
      multipleBasis
      freeRentMonths
      freeRentTypeId
      expirationDate
      currencyCode
      confidential
      furnished
      financialStatus
      tenantImprovementAllowance
      cappedServiceCharges
      commencementDate
      termInMonths
      transactionType
      floor
      suite
      timeMeasurement
      taxAndOpex
      tax
      opex
      netEffectiveRentCustom
      netEffectiveRent
      netEffectiveRentMonthly
      additionalConcessions
      additionalConcessionsPSF
      additionalConcessionsPSM
      comments
      cleaningIncluded
      newToMarket
      createdAt
      updatedAt
      createdUser {
        ${USER_BASIC_FIELDS}
      }
      updatedUser {
        ${USER_BASIC_FIELDS}
      }
      tenantInMarketId
      tenantCompany {
        ${ID_NAME_FIELDS}
      }
      tenantBrokers {
        ${BROKER_ID_NAME_FIELDS}
      }
      landlordBrokers {
        ${BROKER_ID_NAME_FIELDS}
      }
      spaceUse {
        ${ID_NAME_FIELDS}
      }
      property {
        ${ID_NAME_FIELDS}
        primaryAddress
        displayAddress
        type
        propertyCountry {
          ${COUNTRY_FIELDS}
        }
        latitude
        longitude
        latitudeDisplay
        longitudeDisplay
        market {
          ${ID_NAME_FIELDS}
        }
        submarket {
          ${ID_NAME_FIELDS}
        }
        micromarket {
          ${ID_NAME_FIELDS}
        }
        propertyType {
          ${ID_NAME_FIELDS}
        }
        propertyClass {
          ${ID_NAME_FIELDS}
        }
        coverMediaUrl
        coverMediaType
      }
      sublessor {
        ${ID_NAME_FIELDS}
      }
      securityDeposit
      thirdPartyIds {
        thirdPartyTypeId
        value
      }
      securityDepositType {
        ${ID_NAME_FIELDS}
      }
    }
  }
  ${LEASE_OPTION_FRAGMENT}
`;

export const LEASES_ANALYTICS_METADATA = gql`
  query leasesAnalyticsMetadata($search: LeasesAnalyticsMetadataInput) {
    leasesAnalyticsMetadata(search: $search) {
      averageRentFS
      averageRentFSMonthly
      averageRentNNN
      averageRentNNNMonthly
      averageFreeRent
      averageTerm
      averageTIPsf
      numberLeases
      sumLeaseSize
      sumLeaseSizeMt
      averageLeaseSize
      averageLeaseSizeMt
      totalTenantInMarket
      averageRentAtExpiration
      averageRentFSAtExpiration
      averageRentFSAtExpirationMonthly
      averageRentNNNAtExpiration
      averageRentNNNAtExpirationMonthly
      averageNetEffectiveRentFS
      averageNetEffectiveRentNNN
    }
  }
`;

export const LEASES_ANALYTICS_CONCESSION_QUERY = gql`
  query leasesAnalyticsConcession($search: LeasesAnalyticsConcessionInput) {
    leasesAnalyticsConcession(search: $search) {
      slices {
        year
        avgFreeRentMonth
        avgTIAllowance
        quarter
        topItems
        count
      }
    }
  }
`;

export const GET_NER_QUERY = gql`
  query getNetEffectiveRent($input: NetEffectiveRentInput!) {
    getNetEffectiveRent(input: $input)
  }
`;

export const LEASES_LEASING_TRENDS = gql`
  query leases($search: LeaseSearchInput!) {
    leases(search: $search) {
      total
      results {
        id
        buildOutType {
          ${ID_NAME_FIELDS}
        }
        property {
          ${ID_NAME_FIELDS}
          columnSpacingMax
          columnSpacingMaxMt
          columnSpacingMin
          columnSpacingMinMt
          propertyCountry {
            ${COUNTRY_FIELDS}
          }
          coverMediaType
          coverMediaUrl
          displayAddress
          primaryAddress
          currencyCode
          measurementSystem
          latitude
          longitude
          latitudeDisplay
          longitudeDisplay
          locationType {
            ${ID_NAME_FIELDS}
          }
          market {
            ${ID_NAME_FIELDS}
          }
          submarket {
            ${ID_NAME_FIELDS}
          }
          micromarket {
            ${ID_NAME_FIELDS}
          }
          amenities {
            ${ID_NAME_FIELDS}
          }
          owners {
            ${ID_NAME_WEBSITE_LOGO_FIELDS}
          }
          propertyClass {
            ${ID_NAME_FIELDS}
          }
          propertyType {
            ${ID_NAME_FIELDS}
          }
          propertySubtype {
            ${ID_NAME_FIELDS}
          }
          renovatedYears {
            id
            year
          }
          builtYear
          buildingSize
          buildingSizeMt
          floorPlateSize
          floorPlateSizeMt
          parkingRatio
          propertyType {
            ${ID_NAME_FIELDS}
          }
          dataCenter {
            id
            commissionedPower
            underConstPower
            plannedPower
          }
          multifamily {
            id
            totalUnits
            avgUnitSizeMt
            avgUnitSizeSf
          }
          siteCoverageRatio
          industrial {
            id
            driveInDoors
            loadingDocks
            typicalClearHeightMin
            typicalClearHeightMinMt
            typicalClearHeightMax
            typicalClearHeightMaxMt
            sprinklers
            trailerParkingSpaces
          }
          office {
            typicalCeilingHeightFeet
            typicalCeilingHeightInches
            typicalCeilingHeightMt
          }
        }
        tenantCompany {
          ${ID_NAME_FIELDS}
        }
        size
        signDate
        type
        termInMonths
        expirationDate
        leaseType {
          ${ID_NAME_FIELDS}
        }
        measurementSystem
        floor
        rentType
        escalation
        escalationIncrement
        escalationType
        escalationFormatted
        tenantImprovementAllowance
        freeRentMonths
        baseRent
        baseRentNNNEquiv
        baseRentNNNEquivMonthly
        baseRentFullServiceEquiv
        baseRentFullServiceEquivMonthly
        currencyCode
        taxAndOpex
        netEffectiveRent
        netEffectiveRentNNN
        netEffectiveRentCustom
        tenantBrokers {
          ${BROKER_ID_NAME_FIELDS}
        }
        landlordBrokers {
          ${BROKER_ID_NAME_FIELDS}
        }
      }
    }
  }
`;

export const LEASE_TERMS_AVERAGES = gql`
  query leasesAnalyticsMostRecentLeases(
    $search: LeasesAnalyticsMostRecentInput
  ) {
    leasesAnalyticsMostRecentLeases(search: $search) {
      historicalAverage {
        averages {
          date
          totalSizeSF
          totalSizeSquareMeters
          leasesCount
          currencyCode
          measurementSystem
          topItems
          avgBaseRentFullServiceEquiv
          avgBaseRentFullServiceEquivMonthly
          avgBaseRentNNNEquiv
          avgBaseRentNNNEquivMonthly
          avgFreeRent
          avgTIPsf
          avgNetEffectiveRentFullService
          avgNetEffectiveNNN
          avgNetEffectiveRentFullServiceMonthly
          avgNetEffectiveNNNMonthly
        }
      }
    }
  }
`;

export const LEASE_OPTION_TYPES = gql`
  query leaseOptionTypes {
    leaseOptionTypes {
      ${ID_NAME_FIELDS}
    }
  }
`;

export const LEASES_ACTIVITY = gql`
  query leasesActivity($input: LeasesActivityInput!) {
    leasesActivity(input: $input) {
      averageLeaseSize
      leaseCount
      aggregates {
        propertyId
        aggregateLeaseSize
        leaseCount
      }
    }
  }
`;
